import { useQuery } from "@tanstack/react-query";
import { ArrowRight, FileSearch2 } from "lucide-react";
import { useEffect, useMemo } from "react";
import { Link } from "react-router";

import { getScopes, type IScope, ScopeStatus } from "@/api";
import { InlineStatusIndicator } from "@/components/shared/InlineStatusIndicator";
import { Button } from "@/components/ui/button";
import {
    Card,
    CardAction,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import {
    Item,
    ItemContent,
    ItemDescription,
    ItemGroup,
    ItemMedia,
    ItemTitle,
} from "@/components/ui/item";
import { useOrganization } from "@/contexts/frontend/organization/useOrganization";
import { useProcessingScopes } from "@/contexts/frontend/processing-scopes/useProcessingScopes";

import { RECENT_LIMIT, staggerStyle } from "./recentWidget";
import {
    RecentWidgetEmpty,
    RecentWidgetError,
    RecentWidgetSkeleton,
    RelativeTime,
} from "./recentWidgetParts";

const GRID = "grid grid-cols-1 gap-2 sm:grid-cols-2";

function isInProgress(status: ScopeStatus): boolean {
    return status === ScopeStatus.PROCESSING || status === ScopeStatus.PENDING;
}

// Total clauses located across the snapshotted topics — the analysis yield a
// finished scope produced. `scope_topics` is eager-loaded on the list response.
function clausesFound(scope: IScope): number {
    return (scope.scope_topics ?? []).reduce(
        (total, topic) => total + (topic.clauses_found ?? 0),
        0
    );
}

/**
 * Secondary home widget: the 5 most-recent scopes (documents analyzed against a
 * checklist) in a 2-column grid. Each row leads with the scope type icon (like
 * Recent Conversations) carrying a corner status dot from the shared
 * `InlineStatusIndicator` (full status one hover away), with the clause checklist
 * name as secondary text under the title.
 */
export const RecentScopes = () => {
    const { organization } = useOrganization();
    const { addProcessingScope } = useProcessingScopes();

    const { data, isPending, isError, refetch } = useQuery({
        queryKey: ["frontend-organization-recent-scopes", organization.id],
        queryFn: () =>
            getScopes(1, RECENT_LIMIT, {
                sort_by: "created_at",
                order: "desc",
            }),
    });

    const scopes = useMemo(() => data?.items ?? [], [data]);

    // Register in-progress scopes for background polling — the side effect the
    // old per-row `ScopeStatusCell` performed. It drives the auto-invalidation
    // of this query AND the completion/failure toast when a scope finishes.
    // Idempotent (keyed by id); runs in an effect, never during render.
    useEffect(() => {
        for (const scope of scopes) {
            if (isInProgress(scope.status)) {
                addProcessingScope(scope);
            }
        }
    }, [scopes, addProcessingScope]);

    return (
        <Card>
            <CardHeader>
                <CardTitle>Recent scopes</CardTitle>
                <CardDescription>Documents you've analyzed</CardDescription>
                <CardAction>
                    <Button
                        variant="link"
                        size="sm"
                        className="text-muted-foreground hover:text-primary h-auto gap-1 px-1"
                        render={
                            <Link to="/organizations/document-library/documents?type_filter=scopes" />
                        }
                    >
                        View all
                        <ArrowRight className="size-3.5 transition-transform duration-150 group-hover/button:translate-x-0.5 motion-reduce:transition-none" />
                    </Button>
                </CardAction>
            </CardHeader>
            <CardContent>
                {isPending ? (
                    <RecentWidgetSkeleton
                        noun="scopes"
                        count={4}
                        twoLine={false}
                        className={GRID}
                    />
                ) : isError ? (
                    <RecentWidgetError
                        noun="scopes"
                        onRetry={() => refetch()}
                    />
                ) : scopes.length === 0 ? (
                    <RecentWidgetEmpty
                        icon={<FileSearch2 />}
                        title="No scopes yet"
                        description="Create a scope from a document to analyze it against a checklist."
                    />
                ) : (
                    <ItemGroup className={GRID}>
                        {scopes.map((scope, index) => (
                            <ScopeRow
                                key={scope.id}
                                scope={scope}
                                index={index}
                            />
                        ))}
                    </ItemGroup>
                )}
            </CardContent>
        </Card>
    );
};

function ScopeRow({ scope, index }: { scope: IScope; index: number }) {
    const checklistName = scope.clause_checklist?.name;
    // The yield only reads as a result once the analysis has finished.
    const clauseCount =
        scope.status === ScopeStatus.COMPLETE ? clausesFound(scope) : 0;

    return (
        <Item
            render={<Link to={`/organizations/scopes/${scope.id}`} />}
            className="group/row animate-in fade-in-0 slide-in-from-bottom-1 motion-reduce:animate-none"
            style={staggerStyle(index)}
        >
            <ItemMedia
                variant="icon"
                className="bg-muted text-muted-foreground group-hover/row:bg-primary/10 group-hover/row:text-primary relative size-8 self-center rounded-lg transition-colors duration-150 motion-reduce:transition-none"
            >
                <FileSearch2 />
                <InlineStatusIndicator
                    variant="dot"
                    type="scope"
                    status={scope.status}
                    className="ring-card absolute -right-0.5 -bottom-0.5 ring-2"
                />
            </ItemMedia>
            <ItemContent className="min-w-0 gap-0.5">
                <ItemTitle
                    className="block w-full truncate"
                    title={scope.document_title}
                >
                    {scope.document_title}
                </ItemTitle>
                {checklistName || clauseCount > 0 ? (
                    <ItemDescription className="flex min-w-0 items-center gap-1.5">
                        {checklistName ? (
                            <span className="truncate">{checklistName}</span>
                        ) : null}
                        {checklistName && clauseCount > 0 ? (
                            <span
                                aria-hidden
                                className="text-muted-foreground/40"
                            >
                                ·
                            </span>
                        ) : null}
                        {clauseCount > 0 ? (
                            <span className="text-muted-foreground/90 shrink-0 tabular-nums">
                                {clauseCount}{" "}
                                {clauseCount === 1 ? "clause" : "clauses"}
                            </span>
                        ) : null}
                    </ItemDescription>
                ) : null}
            </ItemContent>
            <RelativeTime iso={scope.created_at} />
        </Item>
    );
}
